<template>
  <div id="invoicesPage">
    <!-- Tax invoices (PDF download) — shown only when user has any -->
    <b-card v-if="taxInvoices.length" no-body class="mb-2">
      <div class="px-2 pt-2 pb-1 d-flex align-items-center">
        <feather-icon icon="FileTextIcon" size="20" class="text-primary mr-1" />
        <h5 class="mb-0">Your invoices (PDF)</h5>
        <small class="text-muted ml-1">— card payments via Revolut</small>
      </div>
      <b-table
        :items="taxInvoices"
        :fields="taxFields"
        responsive
        small
        striped
        class="mb-0"
      >
        <template #cell(full_number)="row">
          <strong>{{ formatNumber(row.item) }}</strong>
        </template>
        <template #cell(issued_at)="row">
          {{ formatDate(row.item.issued_at) }}
        </template>
        <template #cell(amount)="row">
          {{ Number(row.item.gross_amount).toFixed(2) }} {{ row.item.currency }}
        </template>
        <template #cell(vat)="row">
          <span v-if="row.item.reverse_charge" class="text-success">
            <feather-icon icon="CheckCircleIcon" size="14" /> Reverse charge
          </span>
          <span v-else-if="Number(row.item.vat_amount) > 0">
            {{ formatRate(row.item.vat_rate) }}% ({{ Number(row.item.vat_amount).toFixed(2) }})
          </span>
          <span v-else class="text-muted">Export 0%</span>
        </template>
        <template #cell(actions)="row">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="downloadingId === row.item.id"
            @click="downloadPdf(row.item)"
          >
            <b-spinner v-if="downloadingId === row.item.id" small class="mr-50" />
            <feather-icon v-else icon="DownloadIcon" size="14" class="mr-25" />
            PDF
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Legacy credit log (existing) -->
    <b-card no-body>
      <div class="px-2 pt-2 pb-1 d-flex align-items-center">
        <feather-icon icon="ListIcon" size="20" class="text-secondary mr-1" />
        <h5 class="mb-0">Credit history</h5>
        <small class="text-muted ml-1">— all balance changes (gift codes, admin adds, deductions, etc.)</small>
      </div>
      <div class="px-2 pb-2">
        <VueDatatable ref="myTable" :setupTable="setupTable">
          <template v-slot:thead>
            <tr>
              <th class="id-col all control"><span>ID</span></th>
              <th>Amount</th>
              <th>
                <b-form-select
                  v-model="search.status"
                  :options="statusOptions"
                  @change="(v) => dataTable.searching(v, 2)"
                  size="sm"
                />
              </th>
              <th>Created</th>
              <th>Order By</th>
              <th class="not-mobile">Notes</th>
            </tr>
          </template>
        </VueDatatable>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BFormSelect, BCard, BTable, BButton, BSpinner } from "bootstrap-vue";
import VueDatatable from "@/components/util/vue-datatable/Index.vue";
import { setupTable, statusOptions } from "@/datatable/invoices.datatable.js";
import Billing from "@/services/billing.service.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "InvoicesPage",
  data: () => ({
    search: { status: null },
    dataTable: {},
    setupTable,
    statusOptions,
    taxInvoices: [],
    downloadingId: null,
    taxFields: [
      { key: "full_number", label: "Number" },
      { key: "issued_at",   label: "Issued" },
      { key: "amount",      label: "Total" },
      { key: "vat",         label: "VAT" },
      { key: "actions",     label: "" },
    ],
  }),
  async mounted() {
    this.dataTable = this.$refs.myTable;
    this.dataTable.setup({ url: `/api/invoices` });
    this.fetchTaxInvoices();
  },
  methods: {
    async fetchTaxInvoices() {
      try {
        this.taxInvoices = await Billing.listTaxInvoices();
      } catch (e) {
        // No tax invoices endpoint (yet) or no records — silently ignore
        this.taxInvoices = [];
      }
    },
    formatNumber(item) {
      // Backend sends the authoritative full_number (handles both eras:
      // legacy IM-2026-0119 and SmartBill IM-1441 where number is null).
      if (item.full_number) return item.full_number;
      const n = String(item.number).padStart(4, "0");
      return `${item.series}-${item.year}-${n}`;
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    },
    formatRate(rate) {
      const n = Number(rate);
      return Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, "");
    },
    async downloadPdf(item) {
      this.downloadingId = item.id;
      try {
        const blob = await Billing.downloadPdf(item.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${this.formatNumber(item)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Could not download PDF",
            icon: "XIcon",
            text:
              e?.response?.status === 404
                ? "PDF is not yet available — please try again in a minute."
                : "Please try again or contact support.",
            variant: "danger",
          },
        });
      }
      this.downloadingId = null;
    },
  },
  components: {
    BFormSelect,
    BCard,
    BTable,
    BButton,
    BSpinner,
    VueDatatable,
    ToastificationContent,
  },
};
</script>

<style lang="scss" scoped>
#invoicesPage {
  h5 {
    font-size: 1rem;
  }
}
</style>
